import type { CustomerProfile } from '../types/customer.types';
import { supabase } from '@/shared/lib/supabase';

/**
 * Customer service — returns the currently authenticated customer's profile.
 * Reads from Supabase Auth (`auth.uid()`) joined with `customer_profile`
 * (one-to-one with auth user, RLS-scoped).
 *
 * If the auth user exists but the trigger-created `customer_profile` row is
 * missing, returns a minimal stub so the UI still renders. The next save flips
 * it to a real row.
 */

export const customerService = {
  async getCurrent(): Promise<CustomerProfile> {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      throw new Error(`Could not load session: ${sessionError.message}`);
    }
    const session = sessionData.session;
    if (!session) {
      throw new Error('not_authenticated');
    }

    const user = session.user;
    const { data: profile, error } = await supabase
      .from('customer_profile')
      .select(
        'id, full_name, phone, phone_verified_at, company, registration_number, vat_number, billing_address_line1, billing_address_line2, billing_address_city, billing_address_postal_code, billing_address_country, billing_email, billing_email_cc, created_at, updated_at'
      )
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      throw new Error(`Could not load profile: ${error.message}`);
    }

    if (!profile) {
      return {
        userId: user.id,
        fullName:
          (user.user_metadata as { full_name?: string } | null)?.full_name ??
          user.email ??
          'Account',
        email: user.email ?? '',
        contactNumber: '',
        locale: 'en-ZA',
        mfaEnabled: false,
        ricaStatus: 'not-required',
        emailVerified: !!user.email_confirmed_at,
      };
    }

    return {
      userId: profile.id,
      fullName: profile.full_name,
      email: user.email ?? profile.billing_email ?? '',
      contactNumber: profile.phone ?? '',
      companyName: profile.company ?? undefined,
      vatNumber: profile.vat_number ?? undefined,
      billingAddress: profile.billing_address_line1
        ? {
            line1: profile.billing_address_line1,
            line2: profile.billing_address_line2 ?? undefined,
            city: profile.billing_address_city ?? '',
            region: '',
            postalCode: profile.billing_address_postal_code ?? '',
            countryCode: profile.billing_address_country ?? 'ZA',
          }
        : undefined,
      locale: 'en-ZA',
      mfaEnabled: false,
      lastLoginAt: user.last_sign_in_at ?? undefined,
      lastLoginLocation: undefined,
      popiaConsentAt: undefined,
      ricaStatus: 'not-required',
      emailVerified: !!user.email_confirmed_at,
    };
  },
};
